import {
  SPEECH_LOCALES,
  type LanguageCode,
} from "../shared";

export interface SpeechVoiceLike {
  name: string;
  lang: string;
  default?: boolean;
  localService?: boolean;
  voiceURI?: string;
}

function normalizeLocale(locale: string): string {
  return locale.trim().replaceAll("_", "-").toLowerCase();
}

function baseLanguage(locale: string): string {
  return normalizeLocale(locale).split("-")[0] ?? "";
}

/**
 * Finds the strongest same-language Web Speech voice without touching browser
 * globals. Exact locale matches beat base-language matches; an optional
 * preferred name then breaks ties, followed by browser-default/local voices
 * and finally the original list order.
 */
export function findVoiceForLanguage<T extends SpeechVoiceLike>(
  voices: readonly T[],
  language: LanguageCode,
  preferredVoiceName?: string,
): T | undefined {
  const targetLocale = normalizeLocale(SPEECH_LOCALES[language]);
  const targetBase = baseLanguage(targetLocale);
  const preferredName = preferredVoiceName?.trim().toLowerCase();

  return voices
    .map((voice, index) => {
      const locale = normalizeLocale(voice.lang);
      if (baseLanguage(locale) !== targetBase) return null;

      let score = locale === targetLocale ? 100 : 50;
      if (preferredName && voice.name.trim().toLowerCase() === preferredName) {
        score += 20;
      }
      if (voice.default) score += 2;
      if (voice.localService) score += 1;

      return { voice, index, score };
    })
    .filter(
      (candidate): candidate is { voice: T; index: number; score: number } =>
        candidate !== null,
    )
    .sort(
      (left, right) =>
        right.score - left.score || left.index - right.index,
    )[0]?.voice;
}

